import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { Participant, RemoteParticipant } from 'livekit-client'

import { Menu, MenuItem } from '@szhsin/react-menu'

import { Chevron, MoreIcon } from '@/src/assets/icons'
import { ParticipantListItem } from '@/src/components/ParticipantListItem'
import { useMaybeRoomContext } from '@/src/context'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectRoomState } from '@/src/store/slices/room'
import { selectAccessToken } from '@/src/store/slices/token'

export interface GroupListItemProps {
  groupId: string 
  groupName: string 
  participants: (Participant | RemoteParticipant)[] 
  isCurrentUserGroup?: boolean 
  groupOptions?: { id: string | null; name: string }[]
  isExpanded?: boolean
  onToggle?: (groupId: string) => void
}

export function GroupListItem({
  groupId,
  groupName,
  participants,
  isCurrentUserGroup = false,
  groupOptions = [],
  isExpanded: externalIsExpanded,
  onToggle,
}: GroupListItemProps) {
  const { t } = useTranslation()
  const [internalIsExpanded, setInternalIsExpanded] = useState(false)
  
  // Используем внешнее состояние если передано, иначе внутреннее
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded
  
  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle(groupId)
    } else {
      setInternalIsExpanded(!internalIsExpanded)
    }
  }, [onToggle, groupId, internalIsExpanded])
  const [isJoining, setIsJoining] = useState(false)
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)
  const roomContext = useMaybeRoomContext()
  const isAdmin = useAppSelector(selectIsAdmin)
  const { livekit_url } = useAppSelector(selectRoomState)
  const accessToken = useAppSelector(selectAccessToken)

  // Проверяем, является ли локальный участник куратором
  const localIsCurator = roomContext?.localParticipant?.attributes?.isCurator === 'true'

  // Админы и кураторы могут входить в группы
  const canJoinGroup = isAdmin || localIsCurator

  // Обработчик для присоединения к группе
  const handleJoinGroup = useCallback(async () => {
    if (!roomContext?.name || !livekit_url) return

    const identity = roomContext.localParticipant?.identity
    if (!identity) return

    setIsJoining(true)

    try {
      const response = await fetch(
        `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomName: roomContext.name,
            participantIdentity: identity,
            groupId: groupId,
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to join group:', await response.text())
      }
    } catch (error) {
      console.error('Error joining group:', error)
    } finally {
      setIsJoining(false)
    }
  }, [roomContext, livekit_url, accessToken, groupId])

  // Обработчик для выхода из группы в основной зал
  const handleLeaveGroup = useCallback(async () => {
    if (!roomContext?.name || !livekit_url) return

    const identity = roomContext.localParticipant?.identity
    if (!identity) return

    setIsJoining(true)

    try {
      const response = await fetch(
        `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomName: roomContext.name,
            participantIdentity: identity,
            groupId: null, // null означает основной зал
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to leave group:', await response.text())
      }
    } catch (error) {
      console.error('Error leaving group:', error)
    } finally {
      setIsJoining(false)
    }
  }, [roomContext, livekit_url, accessToken])

  const filteredParticipants = useMemo(() => {
    if (!roomContext) return participants

    // Фильтруем участников по поиску, если есть
    return participants.filter((participant) => {
      // Исключаем скрытых участников 
      if (participant.permissions?.hidden) return false
      // Исключаем агентов и рекордеров 
      if (participant.permissions?.agent || participant.permissions?.recorder) return false
      return true
    })
  }, [participants, roomContext])

  const participantCount = filteredParticipants.length

  // Получаем время окончания групп из метаданных комнаты
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  
  // Проверяем, есть ли таймер для этой группы (независимо от isExpanded)
  const hasTimer = useMemo(() => {
    if (!roomContext?.metadata) return false
    
    try {
      const metadata = JSON.parse(roomContext.metadata)
      const groupsDateEnd = metadata.groupsDateEnd
      return !!(groupsDateEnd && typeof groupsDateEnd === 'number' && groupsDateEnd > Date.now())
    } catch (e) {
      return false
    }
  }, [roomContext?.metadata])

  useEffect(() => {
    if (!roomContext?.metadata || !isExpanded) {
      setRemainingTime(null)
      return
    }

    try {
      const metadata = JSON.parse(roomContext.metadata)
      const groupsDateEnd = metadata.groupsDateEnd
      const groupsStartTime = metadata.groupsStartTime
      const groupsDuration = metadata.groupsDuration

      if (!groupsDateEnd || typeof groupsDateEnd !== 'number') {
        setRemainingTime(null)
        return
      }

      // Вычисляем оставшееся время
      // Если есть информация о начале и длительности, используем её для более точного расчёта
      const calculateRemainingMs = () => {
        const now = Date.now()
        
        if (groupsStartTime && groupsDuration) {
          // Вычисляем сколько времени прошло с начала
          const elapsed = now - groupsStartTime
          // Оставшееся время = общая длительность - прошедшее время
          const remaining = (groupsDuration * 1000) - elapsed
          return Math.max(0, remaining)
        }
        
        // Fallback: используем groupsDateEnd напрямую
        return Math.max(0, groupsDateEnd - now)
      }

      const updateTimer = () => {
        const diff = calculateRemainingMs()

        if (diff <= 0) {
          setRemainingTime(null)
          return
        }

        // Преобразуем миллисекунды в читаемый формат
        const hours = Math.floor(diff / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        let timeString = ''
        if (hours > 0) {
          timeString = `${hours}ч ${minutes}м ${seconds}с`
        } else if (minutes > 0) {
          timeString = `${minutes}м ${seconds}с`
        } else {
          timeString = `${seconds}с`
        }

        setRemainingTime(timeString)
      }

      // Обновляем таймер сразу
      updateTimer()

      // Обновляем каждую секунду
      const interval = setInterval(updateTimer, 1000)

      return () => clearInterval(interval)
    } catch (e) {
      console.error('Error parsing group timer from metadata:', e)
      setRemainingTime(null)
    }
  }, [roomContext?.metadata, isExpanded])

  const menuButton = (
    <button
      className="lk-button lk-group-menu-button"
      onClick={(e) => e.stopPropagation()} // Предотвращаем сворачивание/разворачивание группы
      aria-label={t('Дополнительные действия')}
      style={{
        padding: '4px',
        minWidth: 'auto',
        height: '24px',
        width: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.7,
        transition: 'opacity 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0.7'
      }}
    >
      <MoreIcon fill={text_primary_color} width={16} height={16} />
    </button>
  )

  return (
    <div className={`lk-group-item ${isCurrentUserGroup ? 'lk-group-item-current' : ''}`}>
      <div
        className="lk-group-item-header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`${groupName}, ${participantCount} участников`}
      >
        <div className="lk-group-item-header-content">
          <span className="lk-group-item-title">
            {groupName}
            {hasTimer && !isExpanded && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  opacity: 0.7,
                }}
                title="У группы установлен таймер"
              >
                ⏱️
              </span>
            )}
          </span>
          <span className="lk-group-item-count">{participantCount}</span>
        </div>
        <div 
          className="lk-group-item-actions"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginRight: '-4px',
          }}
        >
          {canJoinGroup && (
            <div className="lk-group-menu-wrapper">
              <Menu
                menuButton={menuButton}
                transition
                direction="left"
                align="center"
                viewScroll="auto"
                position="anchor"
                portal
              >
                {isCurrentUserGroup ? (
                  <MenuItem
                    onClick={handleLeaveGroup}
                    disabled={isJoining}
                    style={{ color: `${text_primary_color}` }}
                  >
                    {isJoining ? t('Выход...') : t('moveToMainRoom')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={handleJoinGroup}
                    disabled={isJoining}
                    style={{ color: `${text_primary_color}` }}
                  >
                    {isJoining ? t('Присоединение...') : t('joinGroup')}
                  </MenuItem>
                )}
              </Menu>
            </div>
          )}
          <Chevron
            className={`lk-group-item-chevron ${isExpanded ? 'lk-group-item-chevron-expanded' : ''}`}
            style={{ color: `${text_primary_color}` }}
          />
        </div>
      </div>
      {isExpanded && (
        <div className="lk-group-item-participants">
          {remainingTime && (
            <div
              className="lk-group-timer"
              style={{
                padding: '8px 12px',
                margin: '8px 12px',
                borderRadius: '4px',
                backgroundColor: primary_color 
                  ? `color-mix(in srgb, ${primary_color} 15%, transparent)` 
                  : 'rgba(255, 255, 255, 0.1)',
                color: text_primary_color || '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                textAlign: 'center',
                border: `1px solid ${primary_color || 'transparent'}`,
              }}
            >
              {t('groupWillFinishIn', { time: remainingTime })}
            </div>
          )}
          {filteredParticipants.length > 0 ? (
            filteredParticipants.map((participant) => (
              <ParticipantListItem
                key={participant.identity}
                participant={participant}
                groupOptions={groupOptions}
              />
            ))
          ) : (
            <div className="lk-group-item-empty">{t('emptyGroupMessage')}</div>
          )}
        </div>
      )}
    </div>
  )
}

